import { useEffect, useMemo, useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, KeyRound, Plus, RefreshCw, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import StoreLayout from "./StoreLayout";
import {
  createStoreGoogleCalendarEvent,
  deleteStoreGoogleCalendarEvent,
  getCurrentStoreUser,
  getStoreGoogleCalendar,
  listStoreGoogleCalendarEvents,
  logoutStore,
  startStoreGoogleCalendarOAuth,
  StoreApiError,
  StoreGoogleCalendarEvent,
  StoreGoogleCalendarEventPayload,
  StoreGoogleCalendarSetting,
  StoreUser,
  updateStoreGoogleCalendar,
  updateStoreGoogleCalendarEvent,
} from "./api";
import { preferredStoreLocationId, rememberedStoreLocationId } from "./storeLocationPreference";
import { RangeMode, getPreferredCalendarRangeMode, setPreferredCalendarRangeMode } from "@/utils/storeCalendarDirectory";
import {
  GoogleCalendarEventForm,
  GoogleCalendarEventCalendarView,
  GoogleCalendarEventFormModal,
  defaultGoogleCalendarEventForm,
  googleCalendarEventPayloadFromForm,
  googleCalendarEventToForm,
  toggleGoogleCalendarAllDayForm,
} from "@/components/googleCalendar/GoogleCalendarEventControls";

/**
 * 門市日曆——門市端頁面。
 *
 * 門市不用自己設定任何東西：系統依照登入者目前所屬的門市，自動去對照表查出總管理處
 * 幫這間門市設定好的 Google 日曆 ID，直接顯示出來。日曆 ID 只能在總管理端的「門市日曆」
 * 頁面設定，門市端這裡是唯讀的——避免貼錯、被亂改。
 *
 * 對照表由後端 StoreGoogleCalendarSetting 資料表提供，總管理端設定後門市端即可讀取。
 */

export default function StoreGoogleCalendar() {
  const navigate = useNavigate();
  const [user, setUser] = useState<StoreUser | null>(null);
  const [calendar, setCalendar] = useState<StoreGoogleCalendarSetting | null>(null);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(() => rememberedStoreLocationId());
  const [rangeMode, setRangeMode] = useState<RangeMode>(() => getPreferredCalendarRangeMode("store"));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [googleEmail, setGoogleEmail] = useState("");
  const [calendarIdInput, setCalendarIdInput] = useState("");
  const [calendarName, setCalendarName] = useState("");
  const [dirty, setDirty] = useState(false);
  const [events, setEvents] = useState<StoreGoogleCalendarEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventSaving, setEventSaving] = useState(false);
  const [eventForm, setEventForm] = useState<GoogleCalendarEventForm | null>(null);
  const [calendarViewDate, setCalendarViewDate] = useState(() => new Date());
  const [message, setMessage] = useState("");

  const effectiveLocationId = useMemo(() => preferredStoreLocationId(user, currentLocationId), [currentLocationId, user]);
  const activeLocationName = useMemo(
    () => calendar?.LocationName || user?.Locations?.find((location) => location.LocationId === effectiveLocationId)?.Name || "",
    [calendar, user, effectiveLocationId],
  );
  const calendarId = calendar?.CalendarId || calendar?.GoogleEmail || "";

  async function load(locationId?: string | null) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentStoreUser();
      setUser(me.User);
      const nextLocationId = preferredStoreLocationId(me.User, locationId || currentLocationId);
      setCurrentLocationId(nextLocationId);
      if (nextLocationId) {
        const calendarData = await getStoreGoogleCalendar(nextLocationId);
        setCalendar(calendarData.Calendar);
        setGoogleEmail(calendarData.Calendar?.GoogleEmail || "");
        setCalendarIdInput(calendarData.Calendar?.CalendarId || calendarData.Calendar?.GoogleEmail || "");
        setCalendarName(calendarData.Calendar?.CalendarName || "");
        setDirty(false);
        if (calendarData.Calendar?.OAuthStatus === "Connected") {
          await loadEvents(nextLocationId);
        } else {
          setEvents([]);
        }
      } else {
        setCalendar(null);
        setGoogleEmail("");
        setCalendarIdInput("");
        setCalendarName("");
        setDirty(false);
        setEvents([]);
      }
    } catch (error) {
      setCalendar(null);
      setMessage(error instanceof Error ? error.message : "門市資料載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (calendar?.OAuthStatus === "Connected") {
      loadEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarViewDate.getFullYear(), calendarViewDate.getMonth(), calendar?.OAuthStatus]);

  async function handleLogout() {
    await logoutStore().catch(() => undefined);
    navigate("/store-login", { replace: true });
  }

  function handleLocationChange(locationId: string) {
    setCurrentLocationId(locationId);
    load(locationId);
  }

  function updateRangeMode(mode: RangeMode) {
    setRangeMode(mode);
    setPreferredCalendarRangeMode("store", mode);
  }

  function moveCalendar(direction: -1 | 1) {
    setCalendarViewDate((current) => {
      const next = new Date(current);
      if (rangeMode === "WEEK") {
        next.setDate(current.getDate() + direction * 7);
      } else {
        next.setMonth(current.getMonth() + direction);
      }
      return next;
    });
  }

  async function loadEvents(locationId = effectiveLocationId) {
    if (!locationId) return;
    setEventsLoading(true);
    try {
      const timeMin = new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() - 1, 1);
      const timeMax = new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() + 2, 1);
      const data = await listStoreGoogleCalendarEvents({ LocationId: locationId, TimeMin: timeMin.toISOString(), TimeMax: timeMax.toISOString() });
      setEvents(data.Events || []);
    } catch (error) {
      setEvents([]);
      setMessage(error instanceof Error ? error.message : "Google 行事曆事件載入失敗。");
    } finally {
      setEventsLoading(false);
    }
  }

  function updateEventForm(patch: Partial<GoogleCalendarEventForm>) {
    setEventForm((current) => (current ? { ...current, ...patch } : current));
  }

  function toggleAllDay(checked: boolean) {
    setEventForm((current) => (current ? toggleGoogleCalendarAllDayForm(current, checked) : current));
  }

  function eventPayloadFromForm(form: GoogleCalendarEventForm): StoreGoogleCalendarEventPayload {
    return {
      LocationId: effectiveLocationId,
      ...googleCalendarEventPayloadFromForm(form),
    };
  }

  async function saveEvent() {
    if (!eventForm) return;
    setEventSaving(true);
    setMessage("");
    try {
      if (eventForm.EventId) {
        const data = await updateStoreGoogleCalendarEvent(eventForm.EventId, eventPayloadFromForm(eventForm));
        setMessage(data.message || "Google 行事曆事件已更新。");
      } else {
        const data = await createStoreGoogleCalendarEvent(eventPayloadFromForm(eventForm));
        setMessage(data.message || "Google 行事曆事件已新增。");
      }
      setEventForm(null);
      await loadEvents();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Google 行事曆事件儲存失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setEventSaving(false);
    }
  }

  async function removeEvent(event: StoreGoogleCalendarEvent) {
    if (!window.confirm(`確定要刪除「${event.Summary}」嗎？此操作會同步刪除 Google 行事曆上的事件。`)) return;
    setEventSaving(true);
    setMessage("");
    try {
      const data = await deleteStoreGoogleCalendarEvent(event.EventId, effectiveLocationId);
      setMessage(data.message || "Google 行事曆事件已刪除。");
      setEventForm(null);
      await loadEvents();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Google 行事曆事件刪除失敗。");
    } finally {
      setEventSaving(false);
    }
  }

  async function saveCalendarSetting() {
    if (!effectiveLocationId) {
      setMessage("請先選擇門市後再儲存。");
      return null;
    }
    setSaving(true);
    setMessage("");
    try {
      const data = await updateStoreGoogleCalendar({
        LocationId: effectiveLocationId,
        GoogleEmail: googleEmail,
        CalendarId: calendarIdInput || googleEmail,
        CalendarName: calendarName || null,
      });
      setCalendar(data.Calendar);
      setGoogleEmail(data.Calendar.GoogleEmail || "");
      setCalendarIdInput(data.Calendar.CalendarId || data.Calendar.GoogleEmail || "");
      setCalendarName(data.Calendar.CalendarName || "");
      setDirty(false);
      setMessage(data.message || "門市 Google 行事曆設定已儲存。");
      return data.Calendar;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "門市 Google 行事曆設定儲存失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function startOAuth() {
    if (!effectiveLocationId) {
      setMessage("請先選擇門市後再授權。");
      return;
    }
    if (dirty || !calendarId) {
      const saved = await saveCalendarSetting();
      if (!saved) return;
    }
    setAuthorizing(true);
    setMessage("");
    try {
      const data = await startStoreGoogleCalendarOAuth(effectiveLocationId);
      const popup = window.open(data.AuthorizationUrl, "_blank", "width=720,height=760");
      if (!popup) {
        setMessage("瀏覽器阻擋了 Google 授權視窗，請允許彈出視窗後再試一次。");
        return;
      }
      popup.opener = null;
      setMessage("Google 授權視窗已開啟。完成後請回到這裡按「重新整理」確認狀態。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Google 授權流程啟動失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setAuthorizing(false);
    }
  }

  const previewCalendarId = calendarId || calendarIdInput || googleEmail;
  const messageIsError = /失敗|錯誤|請先|阻擋|不正確|尚未|逾時|沒有|無法/.test(message);

  return (
    <StoreLayout currentLocationId={effectiveLocationId} onLocationChange={handleLocationChange} onLogout={handleLogout} title="門市日曆" user={user}>
      {message && (
        <div className={`mb-4 rounded-md border px-4 py-3 text-sm font-bold ${messageIsError ? "border-[#f2b8b5] bg-[#fdebea] text-[#8a1f1f]" : "border-[#cfe3d5] bg-[#f4faf5] text-[#245438]"}`}>
          {message}
        </div>
      )}

      {loading ? (
        <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-8 text-center text-[#7b877f]">載入中...</div>
      ) : (
        <div className="rounded-md border border-[#dfe6e1] bg-white p-3">
          <div className="mb-3 rounded-md border border-[#f0dfb8] bg-[#fdf6e3] px-4 py-3 text-sm leading-6 text-[#7a5b12]">
            <p className="font-bold">門市綁定 Google 行事曆。</p>
            <p>若使用 Gmail 主行事曆，Calendar ID 通常可直接填同一個 Gmail；儲存後請用同一組 Google 帳號完成授權。</p>
          </div>
          <div className="mb-3 rounded-md border border-[#dfe6e1] bg-white p-4">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-base font-extrabold text-[#203d32]">{activeLocationName || "門市"} Google 行事曆設定</h2>
                <p className="text-xs font-medium text-[#7b877f]">此設定會同步給總管理端檢視，不需要再請總管理處代填。</p>
              </div>
              {dirty && <span className="w-fit rounded-full bg-[#fdf6e3] px-3 py-1 text-xs font-bold text-[#7a5b12]">尚未儲存</span>}
            </div>
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_220px]">
              <input
                className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                onChange={(event) => {
                  setGoogleEmail(event.target.value);
                  setCalendarIdInput((current) => current || event.target.value);
                  setDirty(true);
                }}
                placeholder="門市 Gmail，例如：store01@gmail.com"
                value={googleEmail}
              />
              <input
                className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                onChange={(event) => {
                  setCalendarIdInput(event.target.value);
                  setDirty(true);
                }}
                placeholder="Calendar ID，通常可填同一個 Gmail"
                value={calendarIdInput}
              />
              <button
                className="inline-flex h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]"
                onClick={() => {
                  setCalendarIdInput(googleEmail);
                  setDirty(true);
                }}
                type="button"
              >
                帶入門市 Gmail
              </button>
            </div>
            <input
              className="mt-3 h-11 w-full rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
              onChange={(event) => {
                setCalendarName(event.target.value);
                setDirty(true);
              }}
              placeholder="日曆名稱（選填，例如：台北店預約表）"
              value={calendarName}
            />
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
              <button
                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={saving}
                onClick={saveCalendarSetting}
                type="button"
              >
                <Save size={15} />
                {saving ? "儲存中" : "儲存設定"}
              </button>
            </div>
          </div>
          <div className="mb-3 rounded-md border border-[#dfe6e1] bg-[#f8faf8] p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                {calendar?.OAuthStatus === "Connected" ? (
                  <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#245438]">
                    <CheckCircle2 size={16} />
                    已授權 Google 行事曆
                  </p>
                ) : calendar?.OAuthStatus === "Error" ? (
                  <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#8a1f1f]">
                    <AlertCircle size={16} />
                    授權異常，請重新授權
                  </p>
                ) : (
                  <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#7a5b12]">
                    <KeyRound size={16} />
                    尚未授權 Google 行事曆
                  </p>
                )}
                <p className="mt-1 truncate text-xs font-medium text-[#7b877f]">
                  {calendar?.OAuthGoogleEmail ? `授權帳號：${calendar.OAuthGoogleEmail}` : googleEmail ? `請使用 ${googleEmail} 完成授權。` : "請先填寫並儲存門市 Gmail。"}
                </p>
                {calendar?.OAuthLastErrorMessage && <p className="mt-1 text-xs font-medium text-[#8a1f1f]">{calendar.OAuthLastErrorMessage}</p>}
              </div>
              <button
                className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-md bg-[#315746] px-3 text-sm font-bold text-white transition hover:bg-[#254536] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={authorizing || saving || (!googleEmail.trim() && !calendarIdInput.trim())}
                onClick={startOAuth}
                type="button"
              >
                <KeyRound size={15} />
                {authorizing ? "開啟中" : calendar?.OAuthStatus === "Connected" ? "重新授權" : "授權 Google 行事曆"}
              </button>
            </div>
          </div>
          <div className="mb-3 flex flex-col gap-3 px-1 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h2 className="truncate text-base font-extrabold text-[#203d32]">{activeLocationName} 的日曆</h2>
              <p className="truncate text-xs font-medium text-[#7b877f]">{previewCalendarId || "尚未設定 Calendar ID"}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {calendar?.OAuthStatus === "Connected" && (
                <>
                  <button className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-[#315746] px-3 text-sm font-bold text-white hover:bg-[#254536]" onClick={() => setEventForm(defaultGoogleCalendarEventForm())} type="button">
                    <Plus size={15} />
                    新增事件
                  </button>
                  <button className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" disabled={eventsLoading} onClick={() => loadEvents()} type="button">
                    <RefreshCw size={15} />
                    更新事件
                  </button>
                </>
              )}
              <label className="inline-flex items-center gap-2 text-sm font-bold text-[#59665f]">
                檢視範圍
                <select
                  className="h-9 rounded-md border border-[#cfd8d2] px-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                  onChange={(event) => updateRangeMode(event.target.value as RangeMode)}
                  value={rangeMode}
                >
                  <option value="WEEK">週</option>
                  <option value="MONTH">月</option>
                  <option value="AGENDA">議程</option>
                </select>
              </label>
            </div>
          </div>
          {calendar?.OAuthStatus === "Connected" ? (
            <GoogleCalendarEventCalendarView
              events={events}
              loading={eventsLoading}
              rangeMode={rangeMode}
              viewDate={calendarViewDate}
              onEdit={(event) => setEventForm(googleCalendarEventToForm(event))}
              onMove={moveCalendar}
              onToday={() => setCalendarViewDate(new Date())}
            />
          ) : (
            <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 rounded-md border border-dashed border-[#e3c98f] bg-[#fff9ed] px-6 py-10 text-center">
              <AlertTriangle className="text-[#8a5b14]" size={28} />
              <p className="text-base font-bold text-[#7a5610]">尚未完成 Google 行事曆授權</p>
              <p className="max-w-sm text-sm leading-6 text-[#8a6a2f]">請先在上方填寫門市 Gmail / Calendar ID 並完成授權，系統才會用 API 載入事件。</p>
            </div>
          )}
        </div>
      )}
      {eventForm && (
        <GoogleCalendarEventFormModal
          form={eventForm}
          saving={eventSaving}
          onCancel={() => setEventForm(null)}
          onChange={updateEventForm}
          onDelete={
            eventForm.EventId
              ? () => {
                  const target = events.find((item) => item.EventId === eventForm.EventId);
                  if (target) removeEvent(target);
                }
              : undefined
          }
          onMessage={setMessage}
          onSave={saveEvent}
          onToggleAllDay={toggleAllDay}
        />
      )}    </StoreLayout>
  );
}

